<template>
  <div class="vc-line-items">
    <div class="vc-line-items__container">
      <!-- table header -->
      <div v-if="withHeader" class="vc-line-items__head">
        <VcCheckbox
          v-if="selectable"
          :name="$t('common.labels.toggle_vendor_select')"
          :model-value="isAllItemsSelected"
          :indeterminate="isSomeItemsSelected"
          class="vc-line-items__checkbox"
          test-id="vc-line-items-head-checkbox"
          @change="($event) => selectAllItems($event as boolean)"
        />

        <div class="vc-line-items__product">
          {{ $t("common.labels.product") }}
        </div>

        <div
          v-if="showProperties"
          :class="[
            'vc-line-items__properties',
            {
              'vc-line-items__properties--wide': !showPrice,
            },
          ]"
        >
          {{ $t("common.labels.properties") }}
        </div>

        <div v-if="showPrice" class="vc-line-items__price">
          {{ $t("common.labels.price_per_item") }}
        </div>

        <div v-if="$slots.default" class="vc-line-items__slot" :style="{ width: slotWidth }">
          <slot name="titles" />
        </div>

        <div v-if="showTotal" class="vc-line-items__total">
          {{ $t("common.labels.total") }}
        </div>

        <div v-if="removable" class="vc-line-items__removable"></div>
      </div>

      <!-- table body -->
      <div v-if="orderedItems.length">
        <!-- eslint-disable vue/prefer-true-attribute-shorthand -->
        <VueDraggableNext
          :sort="true"
          handle=".handle"
          :force-fallback="true"
          :scroll-sensitivity="200"
          class="vc-line-items__body select-none"
          :list="orderedItems"
          @change="changeItemOrder"
        >
          <transition-group>
            <slot name="line-items">
              <VcLineItem
                v-for="item in orderedItems"
                :key="item.id"
                :image-url="item.imageUrl"
                :name="item.name"
                :route="item.route"
                :properties="item.properties"
                :list-price="item.listPrice"
                :actual-price="item.actualPrice"
                :total="item.extendedPrice"
                :sku="item.sku"
                :with-image="showImage"
                :with-properties="showProperties"
                draggable
                :with-price="showPrice"
                :with-total="showTotal"
                :disabled="disabled"
                :deleted="item.deleted"
                :removable="removable"
                :selectable="selectable"
                :selected="selectable && selectedItemIds?.includes(item.id)"
                :browser-target="browserTarget"
                @select="($event) => selectSingleItem(item.id, $event)"
                @remove="() => removeSingleItem(item.id)"
              >
                <template #before>
                  <slot name="before-content" v-bind="{ item }" />
                </template>

                <template v-if="$slots.default" #default>
                  <div ref="slotElements">
                    <slot v-bind="{ item }" />
                  </div>
                </template>

                <template #after>
                  <slot name="after-content" v-bind="{ item }" />
                </template>
              </VcLineItem>
            </slot>
          </transition-group>
        </VueDraggableNext>
      </div>
      <slot name="after-items" />

      <!-- table footer -->
      <div v-if="selectable || withSubtotal" class="vc-line-items__foot">
        <template v-if="selectable">
          <VcButton
            class="vc-line-items__button vc-line-items__button--desktop"
            size="xs"
            :disabled="!selectedItemIds.length"
            @click="removeSelectedItems"
          >
            {{ $t("common.buttons.remove_selected") }}
          </VcButton>

          <VcButton
            class="vc-line-items__button vc-line-items__button--mobile"
            size="xs"
            variant="outline"
            @click="removeAllItems"
          >
            {{ $t("common.buttons.remove_all") }}
          </VcButton>
        </template>

        <div v-if="withSubtotal" class="vc-line-items__subtotal">
          <span class="vc-line-items__subtotal-label">{{ $t("common.labels.subtotal") }}:</span>
          <span class="vc-line-items__subtotal-sum">{{ $n(subtotal, "currency") }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable vue/no-setup-props-reactivity-loss */
import { intersection, map, sumBy } from "lodash";
import { computed, ref, watchEffect, shallowRef } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { dataChangedEvent, useBroadcast } from "@/shared/broadcast";
import { useModal } from "@/shared/modal";
import { DeleteWishlistProductModal } from "@/shared/wishlists";
import type { PreparedLineItemType } from "@/core/types";

interface IEmits {
  (event: "remove:items", value: string[]): void;
  (event: "select:items", value: { itemIds: string[]; selected: boolean }): void;
  (event: "order:items", value: { itemIds: string[]; order: number }): void;
}

interface IProps {
  disabled?: boolean;
  readonly?: boolean;
  removable?: boolean;
  items?: PreparedLineItemType[];
  sharedSelectedItemIds?: string[];
  selectable?: boolean;
  withImage?: boolean;
  withProperties?: boolean;
  withPrice?: boolean;
  withTotal?: boolean;
  withSubtotal?: boolean;
  withHeader?: boolean;
  browserTarget?: BrowserTargetType;
  listId?: string;
}

const emit = defineEmits<IEmits>();

const props = withDefaults(defineProps<IProps>(), {
  items: () => [],
  withHeader: true,
  browserTarget: "_blank",
});

const slotElements = ref<HTMLElement[]>([]);
const slotWidth = ref<string>("");
const orderedItems = shallowRef(props.items);
const { openModal } = useModal();
const broadcast = useBroadcast();

const showImage = computed(() => props.withImage && props.items.some((item) => item.imageUrl));
const showProperties = computed(() => props.withProperties && props.items.some((item) => item.properties?.length));
const showPrice = computed(() => props.withPrice && props.items.some((item) => item.listPrice || item.actualPrice));
const showTotal = computed(() => props.withTotal && hasTotal.value);

const hasTotal = computed(() => props.items.some((item) => item.extendedPrice));

const subtotal = computed<number>(() =>
  hasTotal.value
    ? sumBy(
        props.items.filter((item) => selectedItemIds.value.includes(item.id) && item.extendedPrice),
        (item) => item.extendedPrice!.amount,
      )
    : 0,
);

const itemIds = computed(() => map(props.items, "id"));
const selectedItemIds = computed(() =>
  props.sharedSelectedItemIds ? intersection(props.sharedSelectedItemIds, itemIds.value) : itemIds.value,
);
const isAllItemsSelected = computed(() => selectedItemIds.value.length === props.items.length);
const isSomeItemsSelected = computed(
  () => selectedItemIds.value.length > 0 && selectedItemIds.value.length < props.items.length,
);

function selectSingleItem(itemId: string, value: boolean) {
  emit("select:items", { itemIds: [itemId], selected: value });
}

function selectAllItems(value: boolean) {
  emit("select:items", { itemIds: itemIds.value, selected: value });
}

// function removeSingleItem(itemId: string) {
//   emit("remove:items", [itemId]);
// }

function removeSelectedItems() {
  emit("remove:items", selectedItemIds.value);
}

function removeAllItems() {
  emit("remove:items", itemIds.value);
}

watchEffect(() => {
  slotWidth.value = slotElements.value[0] ? `${slotElements.value[0].clientWidth}px` : "";
});

function changeItemOrder(item: any) {
  const itemId = item.moved.element.id;
  const newIndex = item.moved.newIndex;

  const currentItems = [...orderedItems.value];
  const oldIndex = currentItems.findIndex((i) => i.id === itemId);

  if (oldIndex === -1) {
    return;
  }

  const [movedItem] = currentItems.splice(oldIndex, 1);
  currentItems.splice(newIndex, 0, movedItem);

  orderedItems.value = currentItems;

  emit("order:items", { itemIds: [itemId], order: newIndex });
}

// Innovadis custom refactor
function removeSingleItem(itemId: string): void {
  const item = props.items.find((i) => itemId.includes(i.id));

  if (item) {
    openModal({
      component: DeleteWishlistProductModal,
      props: {
        listId: props.listId,
        listItem: item,

        onResult() {
          void broadcast.emit(dataChangedEvent);

          orderedItems.value = orderedItems.value.filter((deleted) => deleted.id !== itemId);
        },
      },
    });
  }
}
</script>
